import { useMutation, useQuery, gql } from "@apollo/client";
import clsx from "clsx";
import ResponsiveCDNImage from "../../../components/common/image/responsive-cdn-image";
import { useState, useEffect, useCallback } from "react";
import ReviewDetailModal from "../../../components/review/review-detail-modal";
import { HiOutlinePhoto } from "react-icons/hi2";
import { notify, notifyError } from "../../../utils/toast";
import type {
  GetReviewsQuery,
  GetReviewsQueryVariables,
  UpdateReviewMutation,
  UpdateReviewMutationVariables,
  BulkUpdateReviewMutation,
  BulkUpdateReviewMutationVariables,
} from "../../../__generated__/graphql";

const PAGE_SIZE = 50;

type ReviewRow = NonNullable<
  NonNullable<GetReviewsQuery["getReviews"]>["reviews"][number]
>;

const GET_REVIEWS = gql(`
  query GetReviews($limit: Int, $offset: Int, $status: String, $isDeleted: Boolean, $search: String) {
    getReviews(limit: $limit, offset: $offset, status: $status, isDeleted: $isDeleted, search: $search) {
      ok
      error
      totalCount
      reviews {
        id
        user {
          name
          phone
        }
        store {
          title
        }
        content
        reviewImages {
          img
        }
        createdAt
        orderType
        order {
          amount
        }
        status
        isDeleted
      }
    }
  }
`);

const UPDATE_REVIEW = gql(`
  mutation updateReview($id: Int!, $status: String, $isDeleted: Boolean) {
    updateReview(id: $id, status: $status, isDeleted: $isDeleted) {
      ok
      error
    }
  }
`);

const BULK_UPDATE_REVIEW = gql(`
  mutation bulkUpdateReview($reviewIds: [Int!]!, $status: String!) {
    bulkUpdateReview(reviewIds: $reviewIds, status: $status) {
      ok
      error
    }
  }
`);

const AdminStoreReview = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string>("전체");
  const [isDeletedFilter, setIsDeletedFilter] = useState<string>("전체");
  const [searchKeyword, setSearchKeyword] = useState("");
  const [debouncedKeyword, setDebouncedKeyword] = useState("");
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedReviewIds, setSelectedReviewIds] = useState<Set<number>>(
    new Set(),
  );
  const [focusedReviewIndex, setFocusedReviewIndex] = useState<number | null>(
    null,
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedKeyword(searchKeyword);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchKeyword]);

  const { data, loading, refetch } = useQuery<
    GetReviewsQuery,
    GetReviewsQueryVariables
  >(GET_REVIEWS, {
    variables: {
      limit: PAGE_SIZE,
      offset: (currentPage - 1) * PAGE_SIZE,
      status: statusFilter === "전체" ? undefined : statusFilter,
      isDeleted:
        isDeletedFilter === "전체"
          ? undefined
          : isDeletedFilter === "삭제됨"
          ? true
          : false,
      search: debouncedKeyword || undefined,
    },
  });

  const [updateReview] = useMutation<
    UpdateReviewMutation,
    UpdateReviewMutationVariables
  >(UPDATE_REVIEW);
  const [bulkUpdateReview, { loading: bulkUpdating }] = useMutation<
    BulkUpdateReviewMutation,
    BulkUpdateReviewMutationVariables
  >(BULK_UPDATE_REVIEW);
  const [selectedReviewId, setSelectedReviewId] = useState<number | null>(null);
  const [selectedReviewForModal, setSelectedReviewForModal] =
    useState<ReviewRow | null>(null);

  const handleApprove = async (reviewId: number) => {
    const confirm = window.confirm("승인하시겠습니까?");
    if (!confirm) return;

    try {
      const { data } = await updateReview({
        variables: {
          id: reviewId,
          status: "승인",
        },
      });

      if (data?.updateReview?.ok) {
        notify("승인되었습니다.");
        refetch();
        setSelectedReviewId(null);
        // 필터가 "요청완료"인 경우 첫 페이지로 이동
        if (statusFilter === "요청완료") {
          setCurrentPage(1);
        }
      } else {
        notifyError("처리 중 오류가 발생했습니다.");
      }
    } catch (error) {
      notifyError("처리 중 오류가 발생했습니다.");
    }
  };

  const handleReject = async (reviewId: number) => {
    const confirm = window.confirm("거절하시겠습니까?");
    if (!confirm) return;

    try {
      const { data } = await updateReview({
        variables: {
          id: reviewId,
          status: "거절",
        },
      });

      if (data?.updateReview?.ok) {
        notify("거절되었습니다.");
        refetch();
        setSelectedReviewId(null);
        // 필터가 "요청완료"인 경우 첫 페이지로 이동
        if (statusFilter === "요청완료") {
          setCurrentPage(1);
        }
      } else {
        notifyError("처리 중 오류가 발생했습니다.");
      }
    } catch (error) {
      notifyError("처리 중 오류가 발생했습니다.");
    }
  };

  const handleDeleteToggle = async (reviewId: number) => {
    const confirm = window.confirm("삭제하시겠습니까?");
    if (!confirm) return;

    const { data } = await updateReview({
      variables: {
        id: reviewId,
        isDeleted: true,
      },
    });
    if (data?.updateReview?.ok) {
      notify("삭제되었습니다.");
      refetch();
      // 필터가 "삭제안됨"인 경우 첫 페이지로 이동
      if (isDeletedFilter === "삭제안됨") {
        setCurrentPage(1);
      }
    } else {
      notifyError(
        data?.updateReview?.error ?? "처리 중 오류가 발생했습니다.",
      );
    }
  };

  // 드롭다운 외부 클릭 시 닫기
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest(".dropdown-container")) {
        setSelectedReviewId(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [selectedReviewId]);

  // 선택 관련 핸들러
  const handleSelectAll = useCallback(
    (checked: boolean) => {
      if (checked) {
        // 현재 페이지의 "요청완료" 상태 리뷰만 선택
        const pendingReviews =
          data?.getReviews?.reviews.filter(
            (r: ReviewRow) => r.status === "요청완료",
          ) ?? [];
        setSelectedReviewIds(new Set(pendingReviews.map((r) => r.id)));
      } else {
        setSelectedReviewIds(new Set());
      }
    },
    [data],
  );

  const handleSelectOne = useCallback((reviewId: number, checked: boolean) => {
    setSelectedReviewIds((prev) => {
      const newSelected = new Set(prev);
      if (checked) {
        newSelected.add(reviewId);
      } else {
        newSelected.delete(reviewId);
      }
      return newSelected;
    });
  }, []);

  // 필터 변경 시 첫 페이지로 이동
  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, isDeletedFilter, debouncedKeyword]);

  // 편집 모드 종료 시 선택 초기화
  useEffect(() => {
    if (!isEditMode) {
      setSelectedReviewIds(new Set());
      setFocusedReviewIndex(null);
    } else {
      // 편집 모드 시작 시 첫 번째 "요청완료" 리뷰에 포커스
      const pendingReviews =
        data?.getReviews?.reviews.filter(
          (r: ReviewRow) => r.status === "요청완료",
        ) ?? [];
      if (pendingReviews.length > 0) {
        const firstPendingIndex = data?.getReviews?.reviews.findIndex(
          (r: ReviewRow) => r.id === pendingReviews[0].id,
        );
        if (firstPendingIndex !== undefined && firstPendingIndex !== -1) {
          setFocusedReviewIndex(firstPendingIndex);
        }
      }
    }
  }, [isEditMode, data]);

  // 키보드 단축키 처리
  useEffect(() => {
    if (!isEditMode) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // 입력 필드에 포커스가 있으면 무시
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement ||
        e.target instanceof HTMLSelectElement
      ) {
        return;
      }

      const pendingReviews =
        data?.getReviews?.reviews.filter(
          (r: ReviewRow) => r.status === "요청완료",
        ) ?? [];

      if (pendingReviews.length === 0) return;

      if (e.key === "ArrowDown") {
        e.preventDefault();
        const currentPendingIndices =
          (data?.getReviews?.reviews ?? [])
            .map((r: ReviewRow, idx: number) =>
              r.status === "요청완료" ? idx : -1,
            )
            .filter((idx: number) => idx !== -1);

        if (currentPendingIndices.length === 0) return;

        let nextIndex: number;
        if (focusedReviewIndex === null) {
          nextIndex = currentPendingIndices[0];
        } else {
          const currentIndexInPending =
            currentPendingIndices.indexOf(focusedReviewIndex);
          if (currentIndexInPending === -1) {
            nextIndex = currentPendingIndices[0];
          } else {
            const nextIndexInPending =
              (currentIndexInPending + 1) % currentPendingIndices.length;
            nextIndex = currentPendingIndices[nextIndexInPending];
          }
        }

        setFocusedReviewIndex(nextIndex);

        // 스크롤하여 포커스된 항목이 보이도록
        const element = document.getElementById(`review-row-${nextIndex}`);
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "nearest" });
        }
      } else if (e.key === "Enter") {
        e.preventDefault();
        if (focusedReviewIndex !== null) {
          const focusedReview = data?.getReviews?.reviews[focusedReviewIndex];
          if (focusedReview && focusedReview.status === "요청완료") {
            const isSelected = selectedReviewIds.has(focusedReview.id);
            handleSelectOne(focusedReview.id, !isSelected);
          }
        }
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        const currentPendingIndices =
          (data?.getReviews?.reviews ?? [])
            .map((r: ReviewRow, idx: number) =>
              r.status === "요청완료" ? idx : -1,
            )
            .filter((idx: number) => idx !== -1);

        if (currentPendingIndices.length === 0) return;

        let nextIndex: number;
        if (focusedReviewIndex === null) {
          nextIndex = currentPendingIndices[currentPendingIndices.length - 1];
        } else {
          const currentIndexInPending =
            currentPendingIndices.indexOf(focusedReviewIndex);
          if (currentIndexInPending === -1) {
            nextIndex = currentPendingIndices[currentPendingIndices.length - 1];
          } else {
            const prevIndexInPending =
              currentIndexInPending === 0
                ? currentPendingIndices.length - 1
                : currentIndexInPending - 1;
            nextIndex = currentPendingIndices[prevIndexInPending];
          }
        }

        setFocusedReviewIndex(nextIndex);

        // 스크롤하여 포커스된 항목이 보이도록
        const element = document.getElementById(`review-row-${nextIndex}`);
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "nearest" });
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [
    isEditMode,
    focusedReviewIndex,
    selectedReviewIds,
    data,
    handleSelectOne,
  ]);

  // 일괄 승인/거절 핸들러
  const handleBulkUpdate = async (status: "승인" | "거절") => {
    if (selectedReviewIds.size === 0) {
      notifyError("선택된 리뷰가 없습니다.");
      return;
    }

    const confirmMessage =
      status === "승인"
        ? `선택한 ${selectedReviewIds.size}개의 리뷰를 승인하시겠습니까?`
        : `선택한 ${selectedReviewIds.size}개의 리뷰를 거절하시겠습니까?`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const { data } = await bulkUpdateReview({
        variables: {
          reviewIds: Array.from(selectedReviewIds),
          status,
        },
      });

      if (data?.bulkUpdateReview?.ok) {
        notify(`${status}되었습니다.`);
        setSelectedReviewIds(new Set());
        setIsEditMode(false);
        refetch();
        // 필터가 "요청완료"인 경우 첫 페이지로 이동
        if (statusFilter === "요청완료") {
          setCurrentPage(1);
        }
      } else {
        notifyError(
          data?.bulkUpdateReview?.error ?? "처리 중 오류가 발생했습니다.",
        );
      }
    } catch (error) {
      notifyError("처리 중 오류가 발생했습니다.");
    }
  };

  const pendingReviews =
    data?.getReviews?.reviews.filter(
      (r: ReviewRow) => r.status === "요청완료",
    ) ?? [];
  const isAllSelected =
    pendingReviews.length > 0 &&
    pendingReviews.every((r: ReviewRow) => selectedReviewIds.has(r.id));
  const isIndeterminate =
    selectedReviewIds.size > 0 &&
    selectedReviewIds.size < pendingReviews.length;

  const totalCount = data?.getReviews?.totalCount || 0;
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  if (loading) return <div>로딩 중...</div>;
  if (!data?.getReviews?.ok) return <div>에러가 발생했습니다.</div>;

  return (
    <div className="w-full md:p-8 flex flex-col">
      <h1 className="text-2xl font-bold mb-2 text-gray-800">매장 리뷰</h1>
      <p className="text-gray-600 text-sm mb-6">
        리뷰 승인/거절, 삭제, 일괄 처리를 관리합니다.
      </p>

      {/* 필터 섹션 */}
      <div className="mb-4 flex gap-4 items-center justify-between">
        <div className="flex gap-4 items-center">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">
              승인상태:
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="전체">전체</option>
              <option value="요청완료">요청완료</option>
              <option value="승인">승인</option>
              <option value="거절">거절</option>
            </select>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">
              삭제여부:
            </label>
            <select
              value={isDeletedFilter}
              onChange={(e) => setIsDeletedFilter(e.target.value)}
              className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="전체">전체</option>
              <option value="삭제안됨">삭제안됨</option>
              <option value="삭제됨">삭제됨</option>
            </select>
          </div>
          <input
            type="text"
            placeholder="연락처, 구매자명, 매장명 검색"
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
            className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-64"
          />
          <div className="text-sm text-gray-600">총 {totalCount}개</div>
        </div>
        <div className="flex gap-2 items-center">
          {isEditMode && (
            <>
              <div className="text-sm text-gray-600">
                {selectedReviewIds.size}개 선택됨
              </div>
              <button
                onClick={() => handleBulkUpdate("승인")}
                disabled={selectedReviewIds.size === 0 || bulkUpdating}
                className={clsx(
                  "px-4 py-2 rounded-md text-sm font-medium transition-colors",
                  {
                    "bg-green-500 text-white hover:bg-green-600":
                      selectedReviewIds.size > 0 && !bulkUpdating,
                    "bg-gray-300 text-gray-500 cursor-not-allowed":
                      selectedReviewIds.size === 0 || bulkUpdating,
                  },
                )}
              >
                일괄 승인
              </button>
              <button
                onClick={() => handleBulkUpdate("거절")}
                disabled={selectedReviewIds.size === 0 || bulkUpdating}
                className={clsx(
                  "px-4 py-2 rounded-md text-sm font-medium transition-colors",
                  {
                    "bg-red-500 text-white hover:bg-red-600":
                      selectedReviewIds.size > 0 && !bulkUpdating,
                    "bg-gray-300 text-gray-500 cursor-not-allowed":
                      selectedReviewIds.size === 0 || bulkUpdating,
                  },
                )}
              >
                일괄 거절
              </button>
            </>
          )}
          <button
            onClick={() => setIsEditMode(!isEditMode)}
            className={clsx(
              "px-4 py-2 rounded-md text-sm font-medium transition-colors",
              {
                "bg-blue-500 text-white hover:bg-blue-600": !isEditMode,
                "bg-gray-500 text-white hover:bg-gray-600": isEditMode,
              },
            )}
          >
            {isEditMode ? "편집 완료" : "편집 모드"}
          </button>
        </div>
      </div>

      <div className="overflow-x-auto flex-1">
        <table className="min-w-full bg-white border border-gray-200">
          <thead>
            <tr className="bg-gray-50">
              {isEditMode && (
                <th className="px-4 py-3 border-b text-left font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  <input
                    type="checkbox"
                    checked={isAllSelected}
                    ref={(input) => {
                      if (input) input.indeterminate = isIndeterminate;
                    }}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    className="w-4 h-4"
                  />
                </th>
              )}
              <th className="px-4 py-3 border-b text-left font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                ID
              </th>
              <th className="px-4 py-3 border-b text-left font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                승인상태
              </th>
              <th className="px-4 py-3 border-b text-left font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                삭제여부
              </th>
              <th className="px-4 py-3 border-b text-left font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                현재 노출여부
              </th>
              <th className="px-4 py-3 border-b text-left font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                작성자
              </th>
              <th className="px-4 py-3 border-b text-left font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                연락처
              </th>
              <th className="px-4 py-3 border-b text-left font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                매장명
              </th>
              <th className="px-4 py-3 border-b text-left font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                이미지
              </th>
              <th className="px-4 py-3 border-b text-left font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                내용
              </th>
              <th className="px-4 py-3 border-b text-left font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                주문타입
              </th>
              <th className="px-4 py-3 border-b text-left font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                구매금액
              </th>
              <th className="px-4 py-3 border-b text-left font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                작성일
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {data.getReviews.reviews.map((review: ReviewRow, index: number) => (
              <tr
                key={review.id}
                id={`review-row-${index}`}
                onClick={() => {
                  if (isEditMode && review.status === "요청완료") {
                    setFocusedReviewIndex(index);
                  }
                }}
                className={clsx("hover:bg-gray-50", {
                  "bg-blue-50 ring-2 ring-blue-300":
                    isEditMode && focusedReviewIndex === index,
                  "cursor-pointer": isEditMode && review.status === "요청완료",
                })}
              >
                {isEditMode && (
                  <td className="px-4 py-4 whitespace-nowrap">
                    {review.status === "요청완료" ? (
                      <input
                        type="checkbox"
                        checked={selectedReviewIds.has(review.id)}
                        onChange={(e) =>
                          handleSelectOne(review.id, e.target.checked)
                        }
                        className="w-4 h-4"
                      />
                    ) : (
                      <div className="w-4 h-4"></div>
                    )}
                  </td>
                )}
                <td className="px-4 py-4 whitespace-nowrap">
                  <div className="text-gray-900">{review.id}</div>
                </td>
                <td className="px-4 py-4 whitespace-nowrap">
                  {review.status === "요청완료" ? (
                    <div className="relative dropdown-container">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedReviewId(
                            selectedReviewId === review.id ? null : review.id,
                          );
                        }}
                        className="px-2 py-1 rounded-full bg-orange-100 text-orange-500 hover:bg-orange-200 font-medium transition-colors"
                      >
                        {review.status}
                      </button>
                      {selectedReviewId === review.id && (
                        <div
                          className="absolute top-full left-0 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 z-10"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="flex flex-col gap-2 py-1">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleApprove(review.id);
                              }}
                              className="w-full px-4 py-2 text-left text-green-600 hover:bg-green-50"
                            >
                              승인
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleReject(review.id);
                              }}
                              className="w-full px-4 py-2 text-left text-red-600 hover:bg-red-50"
                            >
                              거절
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <span
                      className={clsx("px-2 py-1 rounded-full font-medium", {
                        "text-green-500 font-semibold":
                          review.status === "승인",
                        "text-red-500 font-semibold": review.status === "거절",
                      })}
                    >
                      {review.status}
                    </span>
                  )}
                </td>
                <td className="px-4 py-4 whitespace-nowrap">
                  <button
                    onClick={() => handleDeleteToggle(review.id)}
                    disabled={
                      review.isDeleted ||
                      review.status === "요청완료" ||
                      review.status === "거절"
                    }
                    className={clsx(
                      "px-2 py-1 rounded-full font-medium transition-colors",
                      {
                        "text-red-600": review.isDeleted,
                        "bg-gray-100 text-gray-800 hover:bg-gray-200":
                          !review.isDeleted && review.status === "승인",
                      },
                    )}
                  >
                    {review.status === "거절" || review.status === "요청완료"
                      ? null
                      : review.isDeleted
                      ? "삭제됨"
                      : "삭제하기"}
                  </button>
                </td>
                <td className="px-4 py-4 whitespace-nowrap">
                  <div
                    className={clsx("text-gray-900", {
                      "text-green-500 font-semibold":
                        review.status === "승인" && !review.isDeleted,
                    })}
                  >
                    {review.status === "승인" && !review.isDeleted
                      ? "노출중"
                      : "미노출"}
                  </div>
                </td>

                <td className="px-4 py-4 whitespace-nowrap">
                  <div className="relative group">
                    <div className="font-medium text-gray-900 max-w-20 truncate cursor-default">
                      {review.user?.name || "익명의 히어로"}
                    </div>
                    <div className="absolute hidden group-hover:block bg-white p-2 rounded shadow-lg border border-gray-200 z-10 left-0 mt-1">
                      <div className="text-sm text-gray-900 whitespace-normal">
                        {review.user?.name || "익명의 히어로"}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-gray-500">
                  {review.user?.phone || "-"}
                </td>
                <td className="px-4 py-4 whitespace-nowrap">
                  <div className="relative group">
                    <div className="font-medium text-gray-900 max-w-20 truncate cursor-default">
                      {review.store?.title}
                    </div>
                    <div className="absolute hidden group-hover:block bg-white p-2 rounded shadow-lg border border-gray-200 z-10 left-0 mt-1">
                      <div className="text-sm text-gray-900 whitespace-normal">
                        {review.store?.title}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-4 whitespace-nowrap">
                  {review.reviewImages && review.reviewImages.length > 0 ? (
                    <div className="relative">
                      <div
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedReviewForModal(review);
                        }}
                        className="cursor-pointer relative"
                      >
                        <ResponsiveCDNImage
                          src={review.reviewImages[0]?.img ?? ""}
                          alt="리뷰 이미지"
                          className="w-16 h-16 rounded-md object-cover"
                          sizeRatio={0.1}
                        />
                        {review.reviewImages.length > 1 && (
                          <div className="absolute bottom-0 right-0 bg-black/70 text-white text-[10px] px-1 py-0.5 rounded flex items-center gap-0.5">
                            <HiOutlinePhoto className="w-2.5 h-2.5" />
                            <span>+{review.reviewImages.length - 1}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="w-16 h-16 bg-gray-200 rounded-md"></div>
                  )}
                </td>
                <td className="px-4 py-4">
                  <div className="text-gray-900 whitespace-pre-wrap max-w-[400px]">
                    {review.content}
                  </div>
                </td>
                <td className="px-4 py-4 whitespace-nowrap">
                  <span
                    className={clsx(
                      "px-2 inline-flex leading-5 font-semibold",
                      {
                        "text-blue-700": review.orderType === "PRODUCT",
                        "text-green-700": review.orderType === "PACK",
                      },
                    )}
                  >
                    {review.orderType === "PACK" ? "예약판매" : "실시간판매"}
                  </span>
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-gray-900">
                  {review.order?.amount != null
                    ? `${review.order.amount.toLocaleString()}원`
                    : "-"}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-gray-500">
                  {new Date(review.createdAt).toLocaleDateString("ko-KR", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* 페이지네이션 */}
      {totalPages > 1 && (
        <div className="mt-4 flex justify-center items-center gap-2">
          <button
            onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
            className={clsx(
              "px-3 py-1 rounded-md text-sm font-medium transition-colors",
              {
                "bg-gray-200 text-gray-400 cursor-not-allowed":
                  currentPage === 1,
                "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50":
                  currentPage > 1,
              },
            )}
          >
            이전
          </button>

          <div className="flex gap-1">
            {(() => {
              const pages: (number | string)[] = [];

              if (totalPages <= 7) {
                // 7페이지 이하면 모두 표시
                for (let i = 1; i <= totalPages; i++) {
                  pages.push(i);
                }
              } else {
                // 첫 페이지
                pages.push(1);

                // 현재 페이지 주변 계산
                const start = Math.max(2, currentPage - 1);
                const end = Math.min(totalPages - 1, currentPage + 1);

                // 시작 부분 생략
                if (start > 2) {
                  pages.push("ellipsis-start");
                }

                // 중간 페이지들
                for (let i = start; i <= end; i++) {
                  if (i !== 1 && i !== totalPages) {
                    pages.push(i);
                  }
                }

                // 끝 부분 생략
                if (end < totalPages - 1) {
                  pages.push("ellipsis-end");
                }

                // 마지막 페이지
                pages.push(totalPages);
              }

              return pages.map((page, idx) => {
                if (page === "ellipsis-start" || page === "ellipsis-end") {
                  return (
                    <span
                      key={`ellipsis-${idx}`}
                      className="px-2 text-gray-400"
                    >
                      ...
                    </span>
                  );
                }
                return (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page as number)}
                    className={clsx(
                      "px-3 py-1 rounded-md text-sm font-medium transition-colors",
                      {
                        "bg-blue-500 text-white": currentPage === page,
                        "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50":
                          currentPage !== page,
                      },
                    )}
                  >
                    {page}
                  </button>
                );
              });
            })()}
          </div>

          <button
            onClick={() =>
              setCurrentPage((prev) => Math.min(totalPages, prev + 1))
            }
            disabled={currentPage === totalPages}
            className={clsx(
              "px-3 py-1 rounded-md text-sm font-medium transition-colors",
              {
                "bg-gray-200 text-gray-400 cursor-not-allowed":
                  currentPage === totalPages,
                "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50":
                  currentPage < totalPages,
              },
            )}
          >
            다음
          </button>
        </div>
      )}

      {/* 리뷰 상세 모달 */}
      {selectedReviewForModal && (
        <ReviewDetailModal
          review={{
            user: {
              maskedName: selectedReviewForModal.user?.name || "익명의 히어로",
            },
            orderType: selectedReviewForModal.orderType,
            createdAt: selectedReviewForModal.createdAt,
            reviewImages: selectedReviewForModal.reviewImages,
            content: selectedReviewForModal.content,
          }}
          onClose={() => setSelectedReviewForModal(null)}
        />
      )}
    </div>
  );
};

export default AdminStoreReview;
